import { Component, computed, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { CameraCaptureComponent } from 'src/app/features/image/components/camera-capture/camera-capture.component';
import { ConfigService } from 'src/app/infrastructure/services/config-services/config.service';
import { DialogService } from '../../dialogs/dialog.service';
import { FileResizeService } from 'src/app/infrastructure/services/file-resize/file-resize.service';
import { ImageBoxLinkComponent } from '../image-box-link/image-box-link.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { SharedModule } from '../../shared.module';
import { _ImageBoxLinkComponent } from '../abstract-classes/_image-box-link.component';
import { photoDropList } from '../models/photo-drop';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';

type PhotoSource = 'camera' | 'gallery';

@Component({
    selector: 'image-box-details',
    templateUrl: './image-box-details.component.html',
    styleUrls: [
        '../image-box.scss',
        './image-box-details.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        ImageBoxLinkComponent,
        CameraCaptureComponent,
        NgTemplateOutlet,
    ],
    animations: [
        showAnimation
    ],
})
export class ImageBoxDetailsComponent
    extends _ImageBoxLinkComponent
{
    @ViewChild('galleryInput') private __galleryInput!: ElementRef<HTMLInputElement>;

    private readonly __configService = inject(ConfigService);
    private readonly __dialogService = inject(DialogService);
    private readonly __fileResizeService = inject(FileResizeService);

    protected override _loadingPageService = inject(LoadingPageService);

    override border$$          = this.input_border$$;
    override guid$$            = this.input_guid$$;
    override guidPath$$        = this._guidPath$$;
    override hideOnLoading$$   = this.input_hideOnLoading$$;
    override loading$$         = this._loading$$;
    override noClick$$         = this.input_noClick$$;
    override size$$            = this.input_size$$;
    override tooltip$$         = this.input_tooltip$$;
    override tooltipPosition$$ = this.input_tooltipPosition$$;
    override url$$             = this.input_url$$;
    override version$$         = this.input_version$$;

    canUpload$$  = input<boolean>(false, { alias: 'canUpload' });

    // Sleep-naar-hoofdfoto: alleen actief mét recht (de parent bepaalt dat). Geen eigen CDK
    // drop-target (meer) — ImageTablistComponent doet zijn eigen pointer-hittest tegen dit element
    // (via [id]="_mainDropId" in het template) en emit rechtstreeks naar _TabPageService.photoDrop$;
    // guid$$() (via _TabPageService.photoGuid$$) toont dan al de gesleepte foto als preview, zie
    // _tab-page.service.ts photoDragPreview_set. dropEnabled$$ bepaalt hier alleen nog of het
    // id'de wrapper-element bestaat (zodat de hittest 'm kan vinden) en of de drag-hint (dragActive$$)
    // getoond wordt.
    dropEnabled$$ = input<boolean>(false, { alias: 'dropEnabled' });

    // True zolang een gesleepte thumbnail boven deze box hangt (zie _TabPageService.photoDragActive$$)
    // — puur een visuele 'geldig drop-doel'-hint, zie image-box-details.component.scss.
    dragActive$$ = input<boolean>(false, { alias: 'dragActive' });

    protected readonly _mainDropId = photoDropList.main;

    protected _showHideEnterTime$$ = computed(() => this.__configService.showHide_enter_time$$() ?? '500ms');
    protected _showHideLeaveTime$$ = computed(() => this.__configService.showHide_leave_time$$() ?? '500ms');

    out_fileSelectEmit = output<File>({ alias: 'fileSelectEmit' });

    // In-pagina camera-overlay (getUserMedia) i.p.v. de externe camera-app; voorkomt dat mobiele
    // browsers de tab herladen of i.p.v. de camera de galerij-picker tonen (zie CameraCaptureComponent).
    cameraOpen$$ = signal(false);

    async emit_fileChange(event: Event): Promise<void> {
        const __file = (event.target as HTMLInputElement).files?.[0];
        if (!__file) return;
        await this.__handleFile(__file);
    }

    // Foto gemaakt in de camera-overlay: overlay sluiten en via hetzelfde pad als galerij-keuze verwerken.
    async emit_cameraCaptured(file: File): Promise<void> {
        this.cameraOpen$$.set(false);
        await this.__handleFile(file);
    }

    emit_cameraClosed(): void {
        this.cameraOpen$$.set(false);
    }

    private async __handleFile(file: File): Promise<void> {
        const __resized = await this.__fileResizeService.resizeIfNeeded(
            file,
            this.__configService.image_MaxWidth$$()  ?? 1920,
            this.__configService.image_MaxHeight$$() ?? 1080,
            this.__configService.image_MaxMB$$()     ?? 2,
        );
        this.out_fileSelectEmit.emit(__resized);
    }

    // Lege box (mag uploaden, nog geen foto): de hele box is klikbaar en opent meteen het
    // camera/galerij-keuzedialoog — geen aparte geneste knop meer nodig. Wel een foto: de
    // klik navigeert naar ImageDetails (onClickEmit). Lege box zonder upload-recht: niet
    // klikbaar, want er is niets om naar ImageDetails te navigeren.
    override emit_onClick(_event: any): void {
        if (!this.guid$$()) {
            if (this.canUpload$$()) this.emit_addPhotoClick();
            return;
        }
        this._out_onClickEmit.emit();
    }

    emit_addPhotoClick(): void {
        this.__dialogService
            .showChoice<PhotoSource>(
                [
                    { label: 'Camera',  value: 'camera'  },
                    { label: 'Galerij', value: 'gallery' },
                ],
                'Image toevoegen',
            )
            .subscribe(source => {
                if (source === 'camera') {
                    this.cameraOpen$$.set(true);
                } else if (source === 'gallery') {
                    this.__galleryInput.nativeElement.value = '';
                    this.__galleryInput.nativeElement.click();
                }
            });
    }
}
